import React, { useState, useEffect, useRef } from 'react';
import './Comments.css';
import { Heart, Pencil, Check, X, Trash2, MapPin } from 'lucide-react';
import { useComments, useUpdateComment, useReactToComment, useDeleteComment, useSetCommentMediaVisibility } from '../hooks/useComments';
import { useCommentsSocketSync } from '../hooks/useCommentsSocketSync';
import { assetUrl } from '../../../config';
import { getErrorMessage } from '../../../shared/errors/getErrorMessage';
import { useAudioHydration } from '../../../shared/hooks/useAudioHydration';

const UserAvatar = ({ name, avatar }) => {
    if (avatar) {
        return (
            <img
                src={avatar}
                alt={name || 'User'}
                loading="lazy"
                style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    aspectRatio: '1/1',
                    display: 'block',
                    backgroundColor: '#333'
                }}
            />
        );
    }
    return (
        <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: '#555',
            color: '#eee',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: '600',
            flexShrink: 0
        }}>
            {(name || '?').charAt(0).toUpperCase()}
        </div>
    );
};

// frontend-restructuring Fase 3 (03.1-auth-store.md §6 grupo 1) -- `toast` llega por prop
// (antes `useToast()` directo) -- `user` ya llegaba por prop desde antes de esta fase.
export const CommentItem = React.memo(({ comment, user, toast }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState(comment.content);
    const editRef = useRef(null);
    const textRef = useRef(null);

    useAudioHydration(textRef, [comment.content]);

    const updateComment = useUpdateComment();
    const reactToComment = useReactToComment();
    const deleteComment = useDeleteComment();
    const setMediaVisibility = useSetCommentMediaVisibility();

    const isAuthor = user && user.id === comment.author_id;
    const reactions = comment.reactions || {};
    const likes = Object.keys(reactions).length;
    const userLiked = user && reactions[user.id];

    const handleSave = () => {
        if (!editRef.current) return;
        const newContent = editRef.current.innerHTML;

        updateComment.mutate({ commentId: comment.comment_id, content: newContent }, {
            onError: (err) => console.error("Update failed", err),
        });

        setIsEditing(false);
    };

    const handleLike = () => {
        if (!user) return;
        reactToComment.mutate({ commentId: comment.comment_id, userId: user.id }, {
            onError: (err) => console.error("Reaction failed", err),
        });
    };

    const handleCancel = () => {
        setEditContent(comment.content);
        setIsEditing(false);
    };

    const handleDelete = () => {
        toast.prompt("¿Segur@ que quieres eliminar este comentario?", {
            confirmText: 'Eliminar',
            onConfirm: () => {
                deleteComment.mutate(comment.comment_id, {
                    // Socket usually handles UI removal, o podemos borrar optimista vía el padre, pero
                    // esperar al socket es más seguro para consistencia
                    onError: (err) => {
                        console.error("Delete failed", err);
                        toast.error("No se pudo eliminar el comentario");
                    },
                });
            },
        });
    };

    // Click en el
    // candado de una imagen/adjunto embebido — el HTML del candado lo arma el backend
    // (resolve()), no un componente React, así que se detecta con un listener delegado sobre
    // el contenedor de dangerouslySetInnerHTML en vez de un onClick por imagen.
    const handleCommentTextClick = (e) => {
        const toggle = e.target.closest('.cy-embed-toggle');
        if (toggle) {
            e.preventDefault();
            e.stopPropagation();
            const wrapper = toggle.closest('[data-media-key]');
            if (!wrapper) return;
            const mediaKey = wrapper.dataset.mediaKey;
            const newAccess = wrapper.dataset.access === 'private' ? 'public' : 'private';
            setMediaVisibility.mutate(
                { commentId: comment.comment_id, mediaKey, access: newAccess, documentId: comment.document_id },
                { onError: (err) => toast.error(getErrorMessage(err)) },
            );
            return;
        }

        // Intercept link clicks
        if (e.target.tagName === 'A' || e.target.closest('a')) {
            const link = e.target.tagName === 'A' ? e.target : e.target.closest('a');
            if (link && link.href) {
                e.preventDefault();
                window.open(link.href, '_blank', 'noopener,noreferrer');
            }
        }

        if (e.target.classList.contains('link-tag') || e.target.closest('.link-tag')) {
            const tag = e.target.classList.contains('link-tag') ? e.target : e.target.closest('.link-tag');
            const url = tag.innerText;
            if (url && (url.startsWith('http') || url.startsWith('www'))) {
                e.preventDefault();
                const finalUrl = url.startsWith('www') ? `https://${url}` : url;
                window.open(finalUrl, '_blank', 'noopener,noreferrer');
            }
        }
    };

    // Comentario anclado a un texto seleccionado en el doc (ver CommentMarginMarkers.jsx) —
    // salta al highlight en el editor y lo resalta un momento, mismo criterio que el
    // resaltado por deep-link `?comment=id` de más abajo, pero a la inversa (de la tarjeta al
    // texto en vez del texto a la tarjeta).
    const jumpToHighlight = () => {
        const span = document.querySelector(`.comment-highlight[data-comment-id="${comment.comment_id}"]`);
        if (!span) return;
        span.scrollIntoView({ behavior: 'smooth', block: 'center' });
        span.classList.add('jump-flash');
        setTimeout(() => span.classList.remove('jump-flash'), 1500);
    };

    return (
        <div className="comment-item" id={`comment-${comment.comment_id}`}>
            <div className="comment-avatar">
                <UserAvatar name={comment.author_name} avatar={comment.author_avatar} />
            </div>
            <div className="comment-content">
                <div className="comment-meta">
                    {comment.anchor_type === 'text_highlight' && (
                        <MapPin
                            size={12}
                            color="#facc15"
                            title="Comentario anclado al texto — click para ver"
                            style={{ cursor: 'pointer' }}
                            onClick={jumpToHighlight}
                        />
                    )}
                    <span className="comment-user">{comment.author_name || 'User'}</span>
                    <span className="comment-date">
                        {new Date(comment.created_at).toLocaleString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })}
                    </span>
                </div>

                {isEditing ? (
                    <div className="comment-edit-wrapper">
                        <div
                            ref={editRef}
                            className="comment-edit-area"
                            contentEditable
                            suppressContentEditableWarning
                            dangerouslySetInnerHTML={{ __html: editContent }}
                        />
                        <div className="comment-edit-actions">
                            <button onClick={handleSave} className="action-btn save"><Check size={14} /></button>
                            <button onClick={handleCancel} className="action-btn cancel"><X size={14} /></button>
                        </div>
                    </div>
                ) : (
                    <div
                        ref={textRef}
                        className="comment-text"
                        dangerouslySetInnerHTML={{ __html: comment.content }}
                        onClick={handleCommentTextClick}
                    />
                )}

                {!isEditing && (
                    <div className="comment-actions">
                        <div className={`reaction-btn ${userLiked ? 'active' : ''}`} onClick={handleLike}>
                            <Heart size={12} fill={userLiked ? "#ef4444" : "none"} color={userLiked ? "#ef4444" : "#666"} />
                            {likes > 0 && <span className="reaction-count">{likes}</span>}
                        </div>
                        {isAuthor && (
                            <>
                                <div className="edit-btn" onClick={() => setIsEditing(true)}>
                                    <Pencil size={12} color="#666" />
                                </div>
                                <div className="edit-btn" onClick={handleDelete} title="Eliminar">
                                    <Trash2 size={12} color="#666" />
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
});

// frontend-restructuring Fase 3 (03.1-auth-store.md §6 grupo 1) -- presentacional, `user`/
// `toast` llegan por props de su contenedor (UniversalCommentModule.jsx) en vez de leer
// `useUser()`/`useToast()` directo.
const Comments = ({ docId, user, toast }) => {
    const [activeTab, setActiveTab] = useState('Conversación');
    const { data: fetchedComments } = useComments(docId);
    const comments = fetchedComments ?? [];
    const commentsEndRef = useRef(null);

    useCommentsSocketSync(docId);

    const highlightedComments = useRef(new Set());
    const prevCommentsLengthRef = useRef(0);
    const prevActiveTabRef = useRef(activeTab);

    useEffect(() => {
        if (activeTab === 'Conversación') {
            const params = new URLSearchParams(window.location.search);
            const targetCommentId = params.get('comment');
            
            if (targetCommentId) {
                const el = document.getElementById(`comment-${targetCommentId}`);
                if (el) {
                    if (!highlightedComments.current.has(targetCommentId)) {
                        highlightedComments.current.add(targetCommentId);
                        
                        // Limpiamos el URL para que al escribir nuevos comentarios sí se aplique el scroll al final
                        params.delete('comment');
                        const newSearch = params.toString();
                        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
                        window.history.replaceState({}, '', newUrl);

                        // Retardo ligero para que cualquier imagen cargue su layout inicial
                        setTimeout(() => {
                            el.scrollIntoView({ behavior: 'smooth', block: 'end' });
                            el.classList.add('highlight-comment');
                            
                            setTimeout(() => {
                                el.classList.remove('highlight-comment');
                            }, 4000);
                        }, 200);

                        prevCommentsLengthRef.current = comments.length;
                        prevActiveTabRef.current = activeTab;
                        return; // Prevenir scroll al final SOLO en el render inicial de la notificación
                    }
                }
            }

            const tabSwitched = prevActiveTabRef.current !== activeTab;
            const commentsAdded = comments.length > prevCommentsLengthRef.current;
            const isInitialLoad = prevCommentsLengthRef.current === 0 && comments.length > 0;

            // Default: Hacemos scroll al fondo SOLO si es tab nuevo, cargó inicial, o aumentó la cantidad.
            // Ignoramos actualizaciones (como dar like o editar) donde la cantidad no cambia.
            if (tabSwitched || commentsAdded || isInitialLoad) {
                setTimeout(() => {
                    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                }, 150);
            }
            
            prevCommentsLengthRef.current = comments.length;
            prevActiveTabRef.current = activeTab;
        } else {
            // Keep tabs in sync when looking away
            prevActiveTabRef.current = activeTab;
        }
    }, [comments, activeTab]);

    const renderEmptyState = (text) => (
        <div className="empty-state-container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ marginLeft: '20px', marginTop: '10px', color: '#666', fontStyle: 'italic' }}>{text}</div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.8 }}>
                <img src={assetUrl('assets/monkey_lost.png')} alt="Empty" style={{ width: '300px' }} />
            </div>
        </div>
    );

    const getFilteredContent = () => {
        switch (activeTab) {
            case 'Team': {
                const authors = [];
                const seen = new Set();
                comments.forEach(c => {
                    if (c.author_id && !seen.has(String(c.author_id))) {
                        seen.add(String(c.author_id));
                        authors.push({ id: c.author_id, name: c.author_name, avatar: c.author_avatar });
                    }
                    // Extracción de etiquetas mencionadas
                    if (c.content && c.content.includes('mention-pill')) {
                        try {
                            const tempDiv = document.createElement('div');
                            tempDiv.innerHTML = c.content;
                            const pills = tempDiv.querySelectorAll('.mention-pill');
                            pills.forEach(pill => {
                                const uid = String(pill.getAttribute('data-uid'));
                                const name = pill.getAttribute('data-name');
                                const avatar = pill.getAttribute('data-avatar');
                                if (uid && uid !== 'null' && uid !== 'undefined' && uid !== 'false' && !seen.has(uid)) {
                                    seen.add(uid);
                                    authors.push({ id: uid, name: name || 'Usuario Etiquetado', avatar: avatar });
                                }
                            });
                        } catch (e) {
                            console.error("Error parsing mentions for team tab", e);
                        }
                    }
                });
                if (authors.length === 0) return renderEmptyState('No team members yet...');
                return (
                    <div className="team-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {authors.map(a => (

                            <div key={a.id} className="team-member-item" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', backgroundColor: '#262626', borderRadius: '8px', border: '1px solid #333' }}>
                                <UserAvatar name={a.name} avatar={a.avatar} />
                                <span className="member-name" style={{ fontSize: '14px', fontWeight: '500', color: '#eee' }}>{a.name || 'Anonymous'}</span>
                            </div>
                        ))}
                    </div>
                );
            }
            case 'Docs': {
                const docs = comments.filter(c => c.content && c.content.includes('class="file-tag"'));
                if (docs.length === 0) return renderEmptyState('No shared documents...');
                return (
                    <div className="docs-list">
                        {docs.map(c => (
                            <div key={c.comment_id} className="doc-item-extract">
                                <div className="comment-meta-sm">Shared by {c.author_name}</div>
                                <div dangerouslySetInnerHTML={{ __html: c.content }} />
                            </div>
                        ))}
                    </div>
                );
            }
            case 'Meetings':
                return renderEmptyState('No meetings recorded...');
            case 'Conversación':
            default:
                if (comments.length === 0) return renderEmptyState('Start the conversation...');
                return (
                    <div className="comments-feed">
                        {comments.map(c => (
                            <CommentItem key={c.comment_id} comment={c} user={user} toast={toast} />
                        ))}
                        <div ref={commentsEndRef} />
                    </div>
                );
        }
    };

    return (
        <div className="comments-module">
            {/* Header */}
            <div className="comments-header">
                {['Conversación', 'Team', 'Docs', 'Meetings'].map(tab => (
                    <span
                        key={tab}
                        className={`header-tag ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab)}
                    >
                        {tab}
                    </span>
                ))}
            </div>

            {/* List */}
            <div className="comments-list-container">
                {getFilteredContent()}
            </div>
        </div>
    );
};

export default Comments;
